import {
  IconArrowRight,
  IconArrowsDiagonal,
  IconGauge,
  IconPhoto,
  IconPlayerPlay,
  IconScale,
  IconSparkles,
  IconStack2,
  IconVideo,
  IconWand,
} from '@tabler/icons-react';
import {
  getLocale,
  isLocaleIndexablePath,
  localizeHref,
  type Locale,
} from '@/lib/locale';
import { Routes } from '@/lib/routes';

type RelatedGrokTool = {
  title: string;
  description: string;
  href: string;
  label: string;
  ctaLabel: string;
  kind:
    | 'generator'
    | 'editor'
    | 'image-to-video'
    | 'reference-to-video'
    | 'video-editor'
    | 'text-to-video'
    | 'extend'
    | 'upscale'
    | 'model'
    | 'limit';
};

type RelatedGrokToolsCopy = {
  eyebrow: string;
  title: string;
  description: string;
  tools: RelatedGrokTool[];
};

const RELATED_GROK_TOOLS: RelatedGrokTool[] = [
  {
    title: 'Grok image generator',
    description:
      'Write a prompt, choose format settings, and generate a new Grok AI image draft.',
    href: Routes.ImageGenerator,
    label: 'Image generator',
    ctaLabel: 'Generate an image',
    kind: 'generator',
  },
  {
    title: 'Grok image to video',
    description:
      'Upload a start frame, describe motion, then generate a short Grok image-to-video draft.',
    href: Routes.ImageToVideo,
    label: 'Image to video',
    ctaLabel: 'Animate an image',
    kind: 'image-to-video',
  },
  {
    title: 'Grok Imagine Video with References',
    description:
      'Upload 1-7 stills to guide identity, product, or style without locking the first frame.',
    href: Routes.ReferenceToVideo,
    label: 'Reference to video',
    ctaLabel: 'Guide a clip with stills',
    kind: 'reference-to-video',
  },
  {
    title: 'Grok video generator',
    description:
      'Write a text scene and turn it into a short Grok AI video draft with format controls.',
    href: Routes.TextToVideo,
    label: 'Text to video',
    ctaLabel: 'Write a video prompt',
    kind: 'text-to-video',
  },
  {
    title: 'Grok video editor',
    description:
      'Start with Grok Imagine, edit an upload or saved video, compare results, and continue with the shared model-aware workflow.',
    href: Routes.AiVideoEditor,
    label: 'Grok video editor',
    ctaLabel: 'Edit with Grok',
    kind: 'video-editor',
  },
  {
    title: 'Grok image-to-image editor',
    description:
      'Upload a reference and make prompt-based Grok image-to-image edits with private uploads and no watermark.',
    href: Routes.Generate,
    label: 'Image editor',
    ctaLabel: 'Create source images',
    kind: 'editor',
  },
  {
    title: 'AI video upscaler',
    description:
      'Upscale a completed MP4 or MOV by 2x or 4x, then compare the source with the saved output.',
    href: Routes.VideoUpscale,
    label: 'Video upscaler',
    ctaLabel: 'Upscale a video',
    kind: 'upscale',
  },
  {
    title: 'AI video extender',
    description:
      'Continue a completed video by 6 or 10 seconds, then review the new scene against the original ending.',
    href: Routes.VideoExtend,
    label: 'Video extender',
    ctaLabel: 'Extend a video',
    kind: 'extend',
  },
];

const RELATED_GROK_TOOLS_BY_LOCALE: Partial<
  Record<Locale, RelatedGrokToolsCopy>
> & { en: RelatedGrokToolsCopy } = {
  en: {
    eyebrow: 'Related tools',
    title: 'Choose the next Grok image or video tool',
    description:
      'Move between the core Grok Imagine workflows without hunting through articles or menus.',
    tools: RELATED_GROK_TOOLS,
  },
  es: {
    eyebrow: 'Herramientas relacionadas',
    title: 'Elige el siguiente tool de imagen o video Grok',
    description:
      'Pasa entre los flujos principales de Grok Imagine sin buscar enlaces dentro de articulos.',
    tools: [
      {
        title: 'Generador de imágenes Grok',
        description:
          'Escribe un prompt, elige el formato y genera un nuevo borrador de imagen Grok AI.',
        href: Routes.ImageGenerator,
        label: 'Generador de imágenes',
        ctaLabel: 'Generar una imagen',
        kind: 'generator',
      },
      {
        title: 'Grok image to video',
        description:
          'Sube un frame inicial, describe el movimiento y genera un borrador corto de imagen a video Grok.',
        href: Routes.ImageToVideo,
        label: 'Imagen a video',
        ctaLabel: 'Animar una imagen',
        kind: 'image-to-video',
      },
      {
        title: 'Grok video generator',
        description:
          'Escribe una escena y conviertela en un borrador corto de video Grok AI con controles de formato.',
        href: Routes.TextToVideo,
        label: 'Texto a video',
        ctaLabel: 'Escribir prompt de video',
        kind: 'text-to-video',
      },
      {
        title: 'Editor de imagen a imagen Grok',
        description:
          'Sube una referencia y realiza ediciones de imagen a imagen con privacidad y sin marca de agua.',
        href: Routes.Generate,
        label: 'Editor de imagen',
        ctaLabel: 'Crear imagen fuente',
        kind: 'editor',
      },
      {
        title: 'Escalador de video IA',
        description:
          'Amplia un MP4 o MOV terminado a 2x o 4x y compara la fuente con el resultado guardado.',
        href: Routes.VideoUpscale,
        label: 'Escalador de video',
        ctaLabel: 'Ampliar un video',
        kind: 'upscale',
      },
    ],
  },
};

const RELATED_TOOL_CTA_BY_CONTEXT: Partial<
  Record<Locale, Record<string, Partial<Record<string, string>>>>
> = {
  en: {
    [Routes.Root]: {
      [Routes.ImageGenerator]: 'Start with a text prompt',
      [Routes.Generate]: 'Edit an existing image',
      [Routes.ImageToVideo]: 'Turn a frame into motion',
      [Routes.ReferenceToVideo]: 'Guide a clip with stills',
      [Routes.TextToVideo]: 'Draft a video scene',
    },
    [Routes.ImageGenerator]: {
      [Routes.Generate]: 'Edit the strongest draft',
      [Routes.ImageToVideo]: 'Animate the best image',
      [Routes.ReferenceToVideo]: 'Guide video with extra stills',
      [Routes.TextToVideo]: 'Write a video scene',
    },
    [Routes.Generate]: {
      [Routes.ImageGenerator]: 'Create from text instead',
      [Routes.ImageToVideo]: 'Animate your edited image',
      [Routes.ReferenceToVideo]: 'Combine several edited stills',
      [Routes.TextToVideo]: 'Plan a text-only clip',
    },
    [Routes.ImageToVideo]: {
      [Routes.ImageGenerator]: 'Create a new start frame',
      [Routes.Generate]: 'Polish the source image',
      [Routes.ReferenceToVideo]: 'Guide video with extra stills',
      [Routes.TextToVideo]: 'Switch to text-to-video',
      [Routes.VideoExtend]: 'Extend a completed video',
      [Routes.VideoUpscale]: 'Upscale a completed video',
    },
    [Routes.ReferenceToVideo]: {
      [Routes.ImageToVideo]: 'Lock a start frame instead',
      [Routes.TextToVideo]: 'Start from text only',
      [Routes.Generate]: 'Prepare reference images',
      [Routes.AiVideoEditor]: 'Edit the finished clip',
      [Routes.VideoExtend]: 'Extend a completed video',
      [Routes.VideoUpscale]: 'Upscale a completed video',
    },
    [Routes.TextToVideo]: {
      [Routes.ImageGenerator]: 'Generate a visual concept',
      [Routes.Generate]: 'Prepare an image asset',
      [Routes.ImageToVideo]: 'Animate an uploaded frame',
      [Routes.ReferenceToVideo]: 'Guide video with extra stills',
      [Routes.VideoExtend]: 'Continue a generated video',
      [Routes.VideoUpscale]: 'Enhance a generated video',
    },
  },
};

const RELATED_TOOL_PRIORITY_BY_CONTEXT: Partial<Record<string, string[]>> = {
  [Routes.Root]: [
    Routes.ImageGenerator,
    Routes.ImageToVideo,
    Routes.TextToVideo,
    Routes.VideoUpscale,
  ],
  [Routes.ImageToVideo]: [
    Routes.ReferenceToVideo,
    Routes.TextToVideo,
    Routes.AiVideoEditor,
    Routes.VideoExtend,
  ],
  [Routes.ReferenceToVideo]: [
    Routes.ImageToVideo,
    Routes.TextToVideo,
    Routes.Generate,
    Routes.AiVideoEditor,
  ],
  [Routes.TextToVideo]: [
    Routes.ImageToVideo,
    Routes.ReferenceToVideo,
    Routes.AiVideoEditor,
    Routes.VideoExtend,
  ],
};

const toolIconMap = {
  generator: IconSparkles,
  editor: IconPhoto,
  'image-to-video': IconPlayerPlay,
  'reference-to-video': IconStack2,
  'text-to-video': IconVideo,
  'video-editor': IconWand,
  extend: IconArrowsDiagonal,
  upscale: IconScale,
  model: IconSparkles,
  limit: IconGauge,
} satisfies Record<RelatedGrokTool['kind'], typeof IconPhoto>;

function getRelatedToolCtaLabel({
  currentPath,
  locale,
  tool,
}: {
  currentPath: string;
  locale: Locale;
  tool: RelatedGrokTool;
}) {
  return (
    RELATED_TOOL_CTA_BY_CONTEXT[locale]?.[currentPath]?.[tool.href] ??
    tool.ctaLabel
  );
}

export function getVisibleRelatedGrokTools({
  currentPath,
  locale,
  tools,
}: {
  currentPath: string;
  locale: Locale;
  tools: RelatedGrokTool[];
}) {
  const visibleTools = tools
    .filter((tool) => tool.href !== currentPath)
    .filter((tool) => isLocaleIndexablePath(tool.href, locale));
  const priority =
    locale === 'en' ? RELATED_TOOL_PRIORITY_BY_CONTEXT[currentPath] : undefined;

  if (priority) {
    visibleTools.sort((left, right) => {
      const leftIndex = priority.indexOf(left.href);
      const rightIndex = priority.indexOf(right.href);
      const leftPriority = leftIndex === -1 ? priority.length : leftIndex;
      const rightPriority = rightIndex === -1 ? priority.length : rightIndex;

      return leftPriority - rightPriority;
    });
  }

  return visibleTools.slice(0, 4);
}

export function RelatedGrokTools({
  currentPath,
  titleAsHeading = true,
}: {
  currentPath: string;
  titleAsHeading?: boolean;
}) {
  const locale = getLocale();
  const copy =
    RELATED_GROK_TOOLS_BY_LOCALE[locale] ?? RELATED_GROK_TOOLS_BY_LOCALE.en;
  const visibleTools = getVisibleRelatedGrokTools({
    currentPath,
    locale,
    tools: copy.tools,
  });

  if (!visibleTools.length) return null;

  return (
    <section className="space-y-5">
      <div className="max-w-3xl">
        <p className="font-semibold text-[#d4af37] text-xs uppercase tracking-[0.2em]">
          {copy.eyebrow}
        </p>
        {titleAsHeading ? (
          <h2 className="mt-2 font-serif text-2xl tracking-normal sm:text-3xl">
            {copy.title}
          </h2>
        ) : (
          <p className="mt-2 font-serif text-2xl tracking-normal sm:text-3xl">
            {copy.title}
          </p>
        )}
        <p className="mt-3 text-white/64 leading-7">{copy.description}</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {visibleTools.map((tool) => {
          const Icon = toolIconMap[tool.kind];

          return (
            <a
              key={tool.href}
              href={localizeHref(tool.href, { locale })}
              className="group flex min-h-52 flex-col justify-between rounded-2xl border border-white/10 bg-[#141414] p-5 transition hover:border-[#d4af37]/35 hover:bg-[#17140a]"
            >
              <div>
                <span className="inline-flex size-11 items-center justify-center rounded-xl border border-[#d4af37]/25 bg-[#d4af37]/10 text-[#d4af37] transition group-hover:border-[#d4af37]/45 group-hover:bg-[#d4af37]/15">
                  <Icon className="size-5" stroke={2} />
                </span>
                <p className="mt-5 font-semibold text-[#f4dc7a] text-xs uppercase tracking-[0.16em]">
                  {tool.label}
                </p>
                {titleAsHeading ? (
                  <h3 className="mt-2 font-semibold text-lg text-white">
                    {tool.title}
                  </h3>
                ) : (
                  <p className="mt-2 font-semibold text-lg text-white">
                    {tool.title}
                  </p>
                )}
                <p className="mt-3 text-sm text-white/62 leading-6">
                  {tool.description}
                </p>
              </div>
              <span className="mt-5 inline-flex items-center gap-2 font-semibold text-[#f0d46c] text-sm">
                {getRelatedToolCtaLabel({ currentPath, locale, tool })}
                <IconArrowRight className="size-4 transition group-hover:translate-x-0.5" />
              </span>
            </a>
          );
        })}
      </div>
    </section>
  );
}
